/**
 * Codes ignorés sur cet appareil (« Ignorer » d'un code inconnu) : les scans suivants ne
 * rouvrent rien et ne comptent rien, jusqu'à la déconnexion ou « Réactiver ».
 * Gardés sur l'appareil pour le compte connecté ; un autre compte repart d'une liste vide.
 */
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'vp_ignored_codes';

type Stored = { userId: string | null; codes: string[] };

const listeners = new Set<() => void>();
let current: Stored = { userId: null, codes: [] };

const read = (): Stored => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Stored>) : null;
    return {
      userId: typeof parsed?.userId === 'string' ? parsed.userId : null,
      codes: Array.isArray(parsed?.codes) ? parsed.codes.filter((c) => typeof c === 'string') : [],
    };
  } catch {
    return { userId: null, codes: [] };
  }
};

if (typeof window !== 'undefined') current = read();

const write = (next: Stored): void => {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Stockage bloqué : la liste vit en mémoire.
  }
  listeners.forEach((listener) => listener());
};

/** Compte connecté : un autre compte ne reprend pas la liste du précédent. */
export const syncIgnoredCodesUser = (userId: string | null): void => {
  if (userId && current.userId !== userId) write({ userId, codes: [] });
};

export const isCodeIgnored = (code: string): boolean => current.codes.includes(code);

export const ignoreCode = (code: string): void => {
  if (!isCodeIgnored(code)) write({ ...current, codes: [code, ...current.codes] });
};

export const reactivateCode = (code: string): void =>
  write({ ...current, codes: current.codes.filter((item) => item !== code) });

export const reactivateAllCodes = (): void => write({ ...current, codes: [] });

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useIgnoredCodes = (): string[] => useSyncExternalStore(subscribe, () => current.codes);
